import type { TimelineSnapshot } from "@/types/burndown.types";
import {
  calculateBreakEvenPlan,
  type BreakEvenPlan,
} from "./break-even.utils";
import {
  getOrBuildCache,
  getDateRangeData,
  type ChartData,
} from "./financial-cache.utils";
import { startOfMonthUTC } from "@burnchart/financial";

function formatGrowthPlanMonthLabel(monthDate: Date): string {
  return monthDate.toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });
}

export type GrowthPlanMode = "ramp" | "customer" | "combined";

export interface GrowthPlanConfig {
  mode: GrowthPlanMode;
  /** Override the calculated linear revenue ramp ($/month). */
  monthlyRampOverride?: number;
}

export interface GrowthPlanPoint {
  label: string;
  date: Date;
  monthIndex: number;
  burn: number;
  baselineRevenue: number;
  projectedRevenue: number;
  netProfit: number;
  cumulativeCash: number;
  isBreakEvenMonth: boolean;
}

export interface GrowthPlanResult {
  points: GrowthPlanPoint[];
  breakEvenMonthIndex: number | null;
  breakEvenDate: Date | null;
  suggestedMonthlyGrowthRatePercent: number | null;
  monthlyRamp: number;
  breakEvenPlan: BreakEvenPlan;
  horizonMonths: number;
}

function addMonthsUTC(date: Date, months: number): Date {
  const next = new Date(date);
  next.setUTCMonth(next.getUTCMonth() + months);
  return next;
}

export function calculateSuggestedCustomerGrowthRatePercent(
  monthlyRamp: number,
  monthlyRevenueBase: number,
): number | null {
  if (monthlyRamp <= 0 || monthlyRevenueBase <= 0) {
    return null;
  }

  const rate = (monthlyRamp / monthlyRevenueBase) * 100;
  return Math.round(Math.min(Math.max(rate, 0.1), 100) * 10) / 10;
}

export function resolveGrowthPlanHorizonMonths(
  breakEvenPlan: BreakEvenPlan,
  fallback = 18,
): number {
  if (breakEvenPlan.monthsBeforeCashZero != null) {
    return Math.max(12, breakEvenPlan.monthsBeforeCashZero + 6);
  }

  if (!breakEvenPlan.isOperatingBreakEven) {
    return Math.max(12, fallback);
  }

  return 12;
}

export function buildGrowthPlanProjection(params: {
  chartData: ChartData;
  currentSnapshot: TimelineSnapshot;
  config: GrowthPlanConfig;
  horizonMonths?: number;
}): GrowthPlanResult | null {
  const breakEvenPlan = calculateBreakEvenPlan(params.currentSnapshot);
  if (!breakEvenPlan) {
    return null;
  }

  const horizonMonths =
    params.horizonMonths ?? resolveGrowthPlanHorizonMonths(breakEvenPlan);

  const monthlyRamp =
    params.config.monthlyRampOverride ??
    breakEvenPlan.requiredMonthlyRevenueRamp ??
    (breakEvenPlan.monthlyRevenueGap > 0
      ? breakEvenPlan.monthlyRevenueGap / Math.max(1, horizonMonths)
      : 0);

  const startDate = startOfMonthUTC(params.currentSnapshot.date);
  const endDate = addMonthsUTC(startDate, horizonMonths - 1);
  const cache = getOrBuildCache(params.chartData);
  const rangeData = getDateRangeData(cache, startDate, endDate);

  const startingRevenue = breakEvenPlan.currentMonthlyRevenue;
  let cumulativeCash = breakEvenPlan.cashBalance;
  let breakEvenMonthIndex: number | null = null;
  const points: GrowthPlanPoint[] = [];

  for (let monthIndex = 0; monthIndex < horizonMonths; monthIndex++) {
    const period = rangeData[monthIndex];
    const monthDate = period?.month ?? addMonthsUTC(startDate, monthIndex);
    const burn = period?.expenses ?? breakEvenPlan.monthlyBurn;
    const baselineRevenue = period?.revenue ?? startingRevenue;
    const rampRevenue = startingRevenue + monthIndex * monthlyRamp;

    let projectedRevenue: number;
    switch (params.config.mode) {
      case "ramp":
        projectedRevenue = rampRevenue;
        break;
      case "customer":
        projectedRevenue = baselineRevenue;
        break;
      case "combined":
        projectedRevenue = Math.max(rampRevenue, baselineRevenue);
        break;
    }

    const netProfit = projectedRevenue - burn;
    cumulativeCash += netProfit;

    const isBreakEvenMonth =
      breakEvenMonthIndex === null && projectedRevenue >= burn && burn > 0;
    if (isBreakEvenMonth) {
      breakEvenMonthIndex = monthIndex;
    }

    points.push({
      label: formatGrowthPlanMonthLabel(monthDate),
      date: monthDate,
      monthIndex,
      burn,
      baselineRevenue,
      projectedRevenue,
      netProfit,
      cumulativeCash,
      isBreakEvenMonth,
    });
  }

  const revenueBaseForSuggestion =
    breakEvenPlan.currentMonthlyRevenue > 0
      ? breakEvenPlan.currentMonthlyRevenue
      : breakEvenPlan.operatingBreakEvenRevenue;

  return {
    points,
    breakEvenMonthIndex,
    breakEvenDate:
      breakEvenMonthIndex != null
        ? (points[breakEvenMonthIndex]?.date ?? null)
        : null,
    suggestedMonthlyGrowthRatePercent: calculateSuggestedCustomerGrowthRatePercent(
      monthlyRamp,
      revenueBaseForSuggestion,
    ),
    monthlyRamp,
    breakEvenPlan,
    horizonMonths,
  };
}
